import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { SUPPLIER_ADMIN_ADMINISTRATORS_CREATE_ROUTE } from "../../../../../../constants/routes"
import { Admin } from "../../../../../users/userTypes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { deleteAdmin } from "../../../../../users/userApi"
import { enqueueSnackbar } from "notistack"
import { UserDetailsDialog } from "../../../../../office/admin/components/UserDetails/UserDetailsDialog"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { CreateOrUpdateAdminState } from "../../../../../office/admin/organization/administrators/tenantAdminTypes"
import { fetchSupplierAdmins, FetchSupplierAdminsResponse, SupplierAdminsQuery } from "../supplierAdminApi"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { UserType } from "../../../../../login/loginTypes"

function transformData(data: FetchSupplierAdminsResponse) {
    return data.administrators
}

export function SupplierAdministratorsTable() {
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [user, setUser] = useState<Admin>()
    const navigate = useNavigate()

    const { l10n } = useAppLocalization()
    const { supplierId } = useCurrentSupplier()

    const supplierAdmins = useApiQuery(fetchSupplierAdmins, {
        queryName: SupplierAdminsQuery.ADMINS_FOR_SUPPLIER,
        dependencies: {
            supplierId,
            userType: UserType.SUPPLIER,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supplierAdmins)

    const { mutate: callDeleteAdmin } = useApiMutation(deleteAdmin, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupplierAdminsQuery.ADMINS_FOR_SUPPLIER] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
        },
    })

    function handleDeleteClick() {
        if (user === undefined) {
            return
        } else {
            callDeleteAdmin({
                userId: user.user.id,
            })
        }
    }

    const columns = useMemo<MRT_ColumnDef<Admin>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("name"),
                accessorKey: "user.displayName",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("email"),
                accessorKey: "user.email",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "is-active",
                    header: l10n.getString("status"),
                },
                (row) => row.user.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="office-manager-user-show-details">
                        <MenuItem
                            key={row.original.user.id}
                            onClick={() => {
                                setUser(row.original)
                                setShowDetailsModal(true)
                                closeMenu()
                            }}
                        >
                            Show details
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="office-manager-user-edit">
                        <MenuItem
                            key={row.original.user.id}
                            onClick={() => {
                                setUser(row.original)
                                const navState: CreateOrUpdateAdminState = { user: row.original }
                                navigate(SUPPLIER_ADMIN_ADMINISTRATORS_CREATE_ROUTE, { state: navState })
                                closeMenu()
                            }}
                        >
                            Edit user
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="office-manager-user-delete">
                        <MenuItem
                            key={row.original.user.id}
                            onClick={() => {
                                setUser(row.original)
                                setShowDeleteModal(true)
                                closeMenu()
                            }}
                        >
                            Delete user
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
            />

            {user && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString("office-manager-user-delete-title")}
                    entityName={user.user.displayName}
                    handleClick={handleDeleteClick}
                    open={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                />
            )}

            {user && (
                <UserDetailsDialog
                    key={user.user.id}
                    userId={user?.user.id}
                    userType="admin"
                    open={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false)
                    }}
                />
            )}
        </>
    )
}
